/**
 * Pure scale helpers for {@link BarChart} — ticks, domains, and zero-baseline bar geometry.
 */

export function calculateNiceTicks(min: number, max: number, numTicks: number = 5): number[] {
  if (max === min) {
    if (max === 0) return [0, 1];
    if (max > 0) return [0, Math.ceil(max)];
    return [Math.floor(min), 0];
  }

  const range = max - min;
  const rawStep = range / (numTicks - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalizedStep = rawStep / magnitude;

  let niceStep: number;
  if (normalizedStep <= 1) niceStep = 1;
  else if (normalizedStep <= 2) niceStep = 2;
  else if (normalizedStep <= 5) niceStep = 5;
  else niceStep = 10;

  niceStep *= magnitude;

  const niceMin = Math.floor(min / niceStep) * niceStep;
  const niceMax = Math.ceil(max / niceStep) * niceStep;

  const ticks: number[] = [];
  for (let tick = niceMin; tick <= niceMax + niceStep * 1e-9; tick += niceStep) {
    ticks.push(Number(tick.toPrecision(12)));
  }
  return ticks;
}

export interface ChartDomain {
  ticks: number[];
  displayMin: number;
  displayMax: number;
  displayRange: number;
  /** True when the plot domain includes negatives and reaches at least 0 (draw a zero baseline). */
  showZeroBaseline: boolean;
}

/** Build a nice domain from data extents, forcing an explicit 0 tick on diverging series. */
export function buildChartDomain(minValue: number, maxValue: number, numTicks: number = 6): ChartDomain {
  let ticks = calculateNiceTicks(minValue, maxValue, numTicks);
  if (minValue < 0 && maxValue > 0 && !ticks.some((t) => Math.abs(t) < 1e-9)) {
    ticks = [...ticks, 0].sort((a, b) => a - b);
  }
  const displayMin = ticks[0]!;
  const displayMax = ticks[ticks.length - 1]!;
  const displayRange = displayMax - displayMin || 1;
  const showZeroBaseline = displayMin < 0 && displayMax >= 0;
  return { ticks, displayMin, displayMax, displayRange, showZeroBaseline };
}

export function valueToY(
  value: number,
  displayMin: number,
  displayRange: number,
  chartHeight: number,
  topPadding: number,
  bottomPadding: number,
): number {
  const plotHeight = chartHeight - topPadding - bottomPadding;
  return chartHeight - bottomPadding - ((value - displayMin) / displayRange) * plotHeight;
}

export function valueToX(
  value: number,
  displayMin: number,
  displayRange: number,
  plotLeft: number,
  plotWidth: number,
): number {
  return plotLeft + ((value - displayMin) / displayRange) * plotWidth;
}

export interface ZeroBaselineBarRect {
  /** Top (vertical) or left (horizontal) of the bar. */
  origin: number;
  /** Height (vertical) or width (horizontal). */
  size: number;
  /** Tip of the bar (value end) — for hover labels. */
  tip: number;
  zero: number;
}

/** Vertical bar growing up/down from y=0. */
export function verticalBarFromZero(
  value: number,
  zeroY: number,
  valueY: number,
): ZeroBaselineBarRect {
  const top = Math.min(zeroY, valueY);
  const bottom = Math.max(zeroY, valueY);
  const size = Math.max(bottom - top, value === 0 ? 0 : 1);
  return {
    origin: top,
    size,
    tip: value >= 0 ? top : bottom,
    zero: zeroY,
  };
}

/** Horizontal bar growing left/right from x=0. */
export function horizontalBarFromZero(
  value: number,
  zeroX: number,
  valueX: number,
): ZeroBaselineBarRect {
  const left = Math.min(zeroX, valueX);
  const right = Math.max(zeroX, valueX);
  const size = Math.max(right - left, value === 0 ? 0 : 1);
  return {
    origin: left,
    size,
    tip: value >= 0 ? right : left,
    zero: zeroX,
  };
}

/** Data extents for a list of numeric values (always includes 0 in the span). */
export function dataExtents(values: readonly number[]): { minValue: number; maxValue: number } {
  if (values.length === 0) return { minValue: 0, maxValue: 0 };
  return {
    minValue: Math.min(...values, 0),
    maxValue: Math.max(...values, 0),
  };
}
